import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Share2, Twitter, Linkedin, ArrowRight, BarChart3, Globe2, Briefcase, Clock } from "lucide-react";
import { toast } from "sonner";
import { freshnessLabel } from "@/lib/time";
import { linkify } from "@/lib/linkify";

const EMBED_ICON = { globe: Globe2, job_exposure: Briefcase };

function SourceLine({ item }) {
  if (!item.source_name) return null;
  return (
    <a href={item.source_url || "#"} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#00F0FF] transition" data-testid="data-item-source">
      Source: {item.source_name}{item.source_date ? ` · ${item.source_date}` : ""} <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function ShareRow({ item }) {
  const url = item.builtin
    ? `${window.location.origin}/ai-progress#${item.anchor}`
    : `${window.location.origin}/data/${item.id}`;
  const text = `${item.title}${item.source_name ? ` — ${item.source_name}` : ""}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Could not copy link"); }
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: item.title, text, url }); return; } catch { /* fallthrough */ } }
    copy();
  };
  const x = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  return (
    <div className="flex items-center gap-1.5" data-testid={`data-item-share-${item.anchor}`}>
      <button onClick={share} className="inline-flex items-center gap-1.5 text-xs font-medium border border-white/10 text-[#cbd5e1] px-3 py-1.5 rounded-md hover:border-[#00F0FF]/50 hover:text-white transition" data-testid={`share-btn-${item.anchor}`}>
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
      <a href={x} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="p-1.5 rounded-md border border-white/10 text-[#94A3B8] hover:text-white hover:border-white/30 transition"><Twitter className="w-3.5 h-3.5" /></a>
      <a href={li} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="p-1.5 rounded-md border border-white/10 text-[#94A3B8] hover:text-white hover:border-white/30 transition"><Linkedin className="w-3.5 h-3.5" /></a>
    </div>
  );
}

// Generic card used for uploaded-image stats and for the built-in teaser items.
export default function DataItemCard({ item }) {
  const isTeaser = item.type === "embedded" && item.link_url;
  const Icon = EMBED_ICON[item.embed_key] || BarChart3;
  return (
    <div id={item.anchor} className="card-surface rounded-2xl overflow-hidden flex flex-col scroll-mt-24" data-testid={`data-item-${item.anchor}`}>
      {/* visual */}
      {item.type === "image" && item.image_url ? (
        <Link to={`/data/${item.id}`} className="block bg-[#0B0F19]" title="Read full article" data-testid={`data-item-image-link-${item.anchor}`}>
          <img src={item.image_url} alt={item.title} className="w-full object-contain max-h-[420px]" loading="lazy" />
        </Link>
      ) : (
        <Link to={item.link_url || "#"} className="relative flex items-center justify-center h-44 bg-gradient-to-br from-[#0e1424] to-[#0b1120] group">
          <Icon className="w-14 h-14 text-[#00F0FF]/60 group-hover:scale-110 transition-transform" />
        </Link>
      )}

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          <div className="flex items-start justify-between gap-3">
            {isTeaser ? (
              <h3 className="font-display font-bold text-lg leading-snug">{item.title}</h3>
            ) : (
              <Link to={`/data/${item.id}`} className="font-display font-bold text-lg leading-snug hover:text-[#00F0FF] transition-colors" data-testid={`data-item-title-link-${item.anchor}`}>{item.title}</Link>
            )}
            {freshnessLabel(item) && (
              <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase px-2 py-1 rounded-full border ${item.builtin ? "border-white/10 text-[#64748B]" : "border-[#00F0FF]/30 text-[#00F0FF] bg-[#00F0FF]/5"}`}
                data-testid={`data-item-freshness-${item.anchor}`}>
                <Clock className="w-3 h-3" /> {freshnessLabel(item)}
              </span>
            )}
          </div>
          {item.description && <p className="text-sm text-[#94A3B8] mt-1.5 whitespace-pre-line break-words line-clamp-4" data-testid={`data-item-desc-${item.anchor}`}>{linkify(item.description)}</p>}
        </div>
        {isTeaser ? (
          <Link to={item.link_url} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#00F0FF] hover:gap-2.5 transition-all" data-testid={`data-item-cta-${item.anchor}`}>
            Explore <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <Link to={`/data/${item.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#00F0FF] hover:gap-2.5 transition-all" data-testid={`data-item-read-${item.anchor}`}>
            Read full article <ArrowRight className="w-4 h-4" />
          </Link>
        )}
        <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
          <SourceLine item={item} />
          <ShareRow item={item} />
        </div>
      </div>
    </div>
  );
}
